<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { format } from 'date-fns'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import ModalNuevo from '@/components/Modals/ModalNuevo/ModalNuevo.vue'
import FoodModalLoading from './FoodModalLoading/FoodModalLoading.vue'
import LoadingImage from '@/components/partials/Loading/LoadingImage.vue'
import Amount from '@/components/partials/Amount.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import Divider from '@/components/partials/Divider.vue'
import FoodAttribute from './FoodAttribute/FoodAttribute.vue'
import AttributeTypes from '@/config/AttributeTypes'
import { useProductHttp } from '@/http/productHttp'
import { useProduct } from '@/composables/product'
import { formatPrice } from '@/utils/helpers'
import { useModalStore } from "@/stores/useModalStore"
import { useCartStore } from "@/stores/useCartStore"
import { useSessionStore } from "@/stores/useSessionStore"
import type { Establishment } from "@/types/http/Establishment"
import { Distribution } from "@/enums/Distribution"
import type { EstablishmentDistribution, Product } from "@/types/http/EstablishmentDistribution"
import { getBreakPoints } from '@/config/breakpoints'
import { storeToRefs } from "pinia"

const props = defineProps<{
  establishment: Establishment
  establishmentDistribution: EstablishmentDistribution
}>()

const { t } = useI18n()
const cartStore = useCartStore()
const sessionStore = useSessionStore()
const route = useRoute()
const isCapacitor = __CAPACITOR__
// const ga = useGa()

const product = ref<Product | null>(null)
const attributes = ref<any[]>([])
const selectedAttributes = ref<Map<any, any>>(new Map())
const amount = ref<number>(1)

const open = computed(() => {
  return !!(route.query.modal === 'food' && route.query.product && route.query.set)
})

const hasItemsFromEstablishment = computed(() => cartStore.establishment && cartStore.establishment.id === props.establishment.id)
const { distribution } = storeToRefs(sessionStore)
const { calculatePriceWithAttributes } = useProduct()

const selectedProductPrice = computed(() => {
  if (!product.value) {
    return 0
  }

  if (product.value.discount.available) {
    return calculatePriceWithAttributes(product.value.discount.new_price, selectedAttributes.value, attributes.value) * amount.value
  }

  // retrieve the price per product and multiply it by the amount
  return calculatePriceWithAttributes(product.value.price, selectedAttributes.value, attributes.value) * amount.value
})

const emit = defineEmits([ 'close', 'awaitEmptyCartConfirmation', 'checkTimeslots', 'productNotAvailable', 'openLocationModal' ])

const updateAttribute = (attributeId, val) => {
  selectedAttributes.value.set(attributeId, val)
}

const router = useRouter()

const close = () => {
  amount.value = 1

  router.replace({ query: { modal: undefined, product: undefined, set: undefined } })
}

const { show } = useModalStore()

const olderThan18Modal = () => {
  return new Promise((resolve) => {
    show({
      dataTestId: 'age-check-modal',
      title: t('modal.ask-age.title'),
      message: t('modal.ask-age.message'),
      closable: false,
      callbacks: [
        {
          label: t('terms.no'),
          dataTestId: 'age-check-modal-no',
          properties: {
            type: 'secondary',
          },
          action: () => {
            resolve(false)
          },
        },
        {
          label: t('terms.yes'),
          dataTestId: 'age-check-modal-yes',
          action: () => {
            resolve(true)
          },
        },
      ],
    })
  })
}

async function addToCart() {
  const location = sessionStore.location

  if (sessionStore.distribution === Distribution.Delivery && location === null) {
    emit('openLocationModal')
    return
  }

  // because we use a Map for no reason, we need to convert it to an array
  // if the value is a string and -1, we remove it because its a placeholder in the select
  // if the value is an object, we need to remove all the false values (checkboxes)
  const filteredAttributes = [ ...selectedAttributes.value ]
      .filter(([ k, v ]) => {
        if (typeof v === 'string') {
          return v !== '-1'
        }

        return true
      })
      .filter(([ k, v ]) => {
        if (typeof v === 'object') {
          for (const [ key, value ] of Object.entries(v)) {
            if (value === false) {
              delete v[key]
            }
          }

          if (Object.keys(v).length === 0) {
            return false
          }
        }

        return true
      })

  // If we close the modal, product will be null. We need to save the data before closing.
  const currentProductData = {
    // use raw data instead of resources to avoid double instantiations and loss of data
    // getters will convert the data into resources
    product: product.value,
    productSetId: route.query.set,
    selectedAttributes: new Map([ ...filteredAttributes ]),
    amount: amount.value,
    distribution: distribution.value,
  }

  // close modal, so other modals can appear
  close()

  // check if a switch in establishments is happening
  if (cartStore.hasCart && !hasItemsFromEstablishment.value) {
    // triggers openEmptyCartConfirmationModal in empty-cart-confirmation-modal.vue
    return emit(
        'awaitEmptyCartConfirmation',
        'switch-establishment',
        (confirm) => {
          if (confirm) {
            cartStore.setEstablishment(props.establishment)
            cartStore.setEstablishmentDistribution(props.establishmentDistribution)
            cartStore.add(currentProductData)
          }
        },
    )
  }

  cartStore.setEstablishment(props.establishment)
  cartStore.setEstablishmentDistribution(props.establishmentDistribution)

  if (product.value?.requires_age_check && !sessionStore.ageCheckApproved) {
    const olderThan18 = await olderThan18Modal()

    if (!olderThan18) {
      return
    }

    sessionStore.setAgeCheckApproved(true)
  }

  const productSet = props.establishmentDistribution.menu.product_sets.find(set => set.id === Number(currentProductData.productSetId))

  const productSetHasTime = productSet?.available_from && productSet?.available_until

  if (productSetHasTime && props.establishmentDistribution.overlay?.can_order) {
    if (productSet !== undefined) {
      const { availableFrom, availableUntil } = productSet

      const now = format(new Date(), 'HH:mm')

      if (now < availableFrom || now > availableUntil) {
        emit('productNotAvailable', availableFrom, availableUntil)
      }
    }
  }

  await cartStore.add(currentProductData)
}

const productHttp = useProductHttp()

const fetchProduct = async (): Promise<Product> => {
  const productId = Number(route.query.product)
  const productSetId = Number(route.query.set)

  const productSet = props.establishmentDistribution.menu.product_sets.find(productSet => productSet.id === productSetId)

  if (!productSet) {
    close()

    return
  }

  const product = productSet.products.find(product => product.id === productId)

  if (!product) {
    close()

    return
  }

  const attributeData = await productHttp.productAttributes(productId, props.establishment.id)

  product.attributes = attributeData

  attributes.value = attributeData

  return product
}

function prepareEmptyAttributesArray(attributes) {
  const emptyAttributesArray = new Map()

  attributes.forEach(attribute => {
    if (attribute.type === AttributeTypes.DropDown) {
      emptyAttributesArray.set(
          String(attribute.id),
          String(attribute.subProducts[0].id),
      )
    } else if (attribute.type === AttributeTypes.CheckBox) {
      emptyAttributesArray.set(
          String(attribute.id),
          attribute.subProducts.reduce((prev, current) => {
            prev[String(current.id)] = false
            return prev
          }, {}),
      )
    } else if (attribute.type === AttributeTypes.OptionalDropdown) {
      emptyAttributesArray.set(
          String(attribute.id),
          '-1',
      )
    } else {
      emptyAttributesArray.set(String(attribute.id), null)
    }
  })

  return emptyAttributesArray
}

const toggleProduct = async (bool) => {
  if (bool) {
    product.value = await fetchProduct()

    selectedAttributes.value = prepareEmptyAttributesArray(attributes.value)

    // ga.trackViewProduct({
    //   product: product,
    //   establishment: props.establishment,
    //   selectedAttributes: selectedAttributes.value,
    // })
  } else {
    product.value = null
    attributes.value = []
  }
}

watch(open, toggleProduct)
onMounted(() => toggleProduct(open.value))

const breakpoints = getBreakPoints()

const imageSrcSet = computed<string | undefined>(() => {
  if (product.value.media.images.cover) {
    return `${product.value.media.images.cover?.small.url} ${breakpoints.sm}w,
          ${product.value.media.images.cover?.medium.url} ${breakpoints.md}w,
          ${product.value.media.images.cover?.large.url} ${breakpoints.lg}w,
          ${product.value.media.images.cover?.medium.url} ${breakpoints.xl}w`
  }

  return undefined
})
</script>

<template>
  <ModalNuevo no-padding
              :open="open"
              class="food-modal"
              data-test-id="food-modal"
              @close="close">
    <div v-if="product"
         data-test-id="add-modal"
         class="food-modal__content"
    >
      <div class="food-modal__img">
        <LoadingImage v-if="product.media.images.cover?.small.url"
                      :src="product.media.images.cover?.small.url"
                      :srcset="imageSrcSet"
                      :loader-height="isCapacitor ? '220px' : '310px'"
        />
      </div>

      <div class="food-modal__info">
        <p class="food-modal__title">
          {{ product.title }}
        </p>
        <div class="food-modal__description">
          {{ product.description }}
        </div>

        <template v-if="attributes.length > 0 && selectedAttributes">
          <Divider type="dashed"/>

          <FoodAttribute v-for="attribute in attributes"
                         :key="`attribute_group_${attribute.id}`"
                         :attribute="attribute"
                         :selection="selectedAttributes.get(String(attribute.id))"
                         @input="(e) => updateAttribute(String(attribute.id), e)"
          />

          <div class="food-modal__surcharge" v-if="product.deposit_surcharge > 0">
            {{ t('messages.has_deposit_surcharge', { amount: product.deposit_surcharge }) }}
          </div>
          <div class="food-modal__surcharge" v-if="product.sup_surcharge > 0">
            {{ t('messages.has_sup_surcharge', { amount: product.sup_surcharge }) }}
          </div>
        </template>
      </div>
    </div>
    <FoodModalLoading v-else/>

    <!-- Buttons in Modal Bottom -->
    <template #modal-bottom>
      <Amount v-model="amount"
              size="lg"
              :min="1"
              data-test-id="add-modal-amount"
      />
      <Button data-test-id="add-to-cart"
              data-hj-allow
              :disabled="!product"
              @click="addToCart"
      >
        {{ t('buttons.add') }} &euro; {{ formatPrice(selectedProductPrice) }}
      </Button>
    </template>
  </ModalNuevo>
</template>

<style lang="scss">
// NOTE: this component is not scoped due to a bug with Vue teleport scoped styling
// NOTE: https://github.com/vuejs/core/issues/2669
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.food-modal {
  // target child component
  .modal__container {
    @include lg-up {
      max-width: 38rem !important;
    }
  }

  &__img img {
    width: 100%;
    object-fit: cover;
    max-height: 220px;

    @include lg-up {
      max-height: 310px;
    }
  }

  &__info {
    padding: 2rem;

    @include lg-up {
      padding: 3rem;
    }
  }

  &__title {
    padding:   0;
    font-weight: 700;
    @include line-lg;
    font-size: 1.125rem;
  }

  &__description {
    padding:   0;
    padding-bottom: 1.25rem;
    color:     var(--color-neutral-secondary);
    font-size: 1rem;
  }

  &__surcharge {
    padding: 0;
    color:   var(--color-neutral-secondary);
    font-size: 0.9rem;
  }

  &__heading {
    font-size: 1.125rem;
    font-weight: 900;
    margin:    0 0 1rem 0;
  }
}
</style>
